<script setup>
import { computed } from 'vue'
import { PhWarning } from '@phosphor-icons/vue'

const props = defineProps({
    title:        { type: String,  default: ''       },
    message:      { type: String,  default: ''       },
    confirmText:  { type: String,  default: 'Delete' },
    cancelText:   { type: String,  default: 'Cancel' },
    isShow:       { type: Boolean, default: false    },
    confirmColor: { type: String,  default: 'red'    }, // red | navy | staff | edit
})

defineEmits(['cancel', 'delete'])

const iconBgClass = computed(() => ({
    red:   'bg-red-50',
    navy:  'bg-primary/10',
    staff: 'bg-primary/10',
    edit:  'bg-edit/10',
}[props.confirmColor] ?? 'bg-red-50'))

const iconColorClass = computed(() => ({
    red:   'text-red-500',
    navy:  'text-primary',
    staff: 'text-primary',
    edit:  'text-edit',
}[props.confirmColor] ?? 'text-red-500'))

const confirmClasses = computed(() => ({
    red:   'bg-red-500 hover:bg-red-600 focus:ring-red-200',
    navy:  'bg-primary hover:bg-primary-light focus:ring-primary/20',
    staff: 'bg-primary hover:bg-primary-light focus:ring-primary/20',
    edit:  'bg-edit hover:bg-edit-light focus:ring-edit/20',
}[props.confirmColor] ?? 'bg-red-500 hover:bg-red-600 focus:ring-red-200'))
</script>

<template>
    <Teleport to="body">
        <Transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="isShow"
                class="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40"
                @click.self="$emit('cancel')"
            >
                <Transition
                    enter-active-class="transition ease-out duration-200"
                    enter-from-class="opacity-0 scale-95 translate-y-1"
                    enter-to-class="opacity-100 scale-100 translate-y-0"
                    leave-active-class="transition ease-in duration-150"
                    leave-from-class="opacity-100 scale-100 translate-y-0"
                    leave-to-class="opacity-0 scale-95 translate-y-1"
                >
                    <div
                        v-if="isShow"
                        class="w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden"
                    >
                        <div class="p-6">
                            <!-- Icon + Title -->
                            <div class="flex flex-col items-center text-center mb-5">
                                <div class="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                                    :class="iconBgClass">
                                    <PhWarning :size="28" weight="duotone" :class="iconColorClass" />
                                </div>

                                <h3 class="text-base font-bold text-gray-800 mb-1.5">
                                    {{ title ? title : confirmText }}
                                </h3>
                                <p class="text-sm text-gray-500 leading-relaxed">
                                    {{ message || `Are you sure you want to ${confirmText.toLowerCase()} this ${title.toLowerCase()}? This action cannot be undone.` }}
                                </p>
                            </div>

                            <!-- Actions -->
                            <div class="flex gap-3">
                                <button
                                    type="button"
                                    @click="$emit('cancel')"
                                    class="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600
                                           bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-200
                                           transition focus:outline-none focus:ring-2 focus:ring-gray-200"
                                >
                                    {{ cancelText }}
                                </button>
                                <button
                                    type="button"
                                    @click="$emit('delete')"
                                    class="flex-1 px-4 py-2.5 text-sm font-semibold text-white
                                           rounded-lg transition focus:outline-none focus:ring-2"
                                    :class="confirmClasses"
                                >
                                    {{ confirmText }}
                                </button>
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>
        </Transition>
    </Teleport>
</template>